import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  computed,
  inject,
  input,
  model,
  output,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { GroupResult, groupBy } from '@progress/kendo-data-query';
import { Observable, of } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { ProjectModel } from '../../../../../core/models/project/project.model';
import { TimeBlockProjectModel } from '../../../../../core/models/timeblock/time-block.model';
import { ProjectTransformationService } from '../../../../../core/services/project-transformation.service';
import { ProjectsHttpService } from '../../../../../core/state/projects/projects-http.service';
import { TasksHttpService } from '../../../../../core/state/projects/tasks/tasks-http.service';
import { ProjectDataService } from '../../../../../pages/project/project-details/state-and-data-handling/project-data.service';
import { ProjectStatusFilter } from '../../../../../pages/project/shared/project-detail-types';
import { timeDialogFormControls } from '../../../../../shared/components/dialogs/date-and-time-input-dialog/date-and-time-input-dialog.constants';
import { DropdownOption, TimeOfDayValues } from '../../../../../shared/data-types/dropdown-option';
import { FormControlNameRecord } from '../../../../../shared/data-types/form-types';
import { TimeBlockSource } from '../../../../../shared/data-types/shared-time-block-types';
import {
  entityToDropdownOption,
  observableToDropdownOptions,
  recordToGroupedDropdownOptions,
} from '../../../../../shared/functions/dropdown-functions';
import { CustomFormValidators } from '../../../../../shared/validators/custom-form.validators';
import { IntervalTypes } from '../../../calendar-types';
import { ITimeBlockComponentItem } from '../../../components/time-block/types/time-block-component-item-types';
import {
  TimeBlockContentType,
  TimeBlockType,
} from '../../../components/time-block/types/time-block-types';

@Component({
  selector: 'app-project-type-dialog',
  templateUrl: './project-type-dialog.component.html',
  standalone: false,
})
export class ProjectTypeDialogComponent implements OnInit, OnDestroy {
  // Inputs
  readonly workTimeStart = input.required<Date>();
  readonly workTimeEnd = input.required<Date>();
  readonly selectedTimeBlock = input<ITimeBlockComponentItem>();
  readonly isInvoiced = input.required<boolean>();
  readonly footer = input.required<TemplateRef<any>>();
  readonly projectTypeForm = model.required<FormGroup>();
  readonly fixIntervalDropdownVisible = model(false);
  readonly noProjects = model<boolean>(null);

  // Outputs
  readonly loading$ = output<boolean>();

  // Signals
  readonly isDisabled = computed(() => this.isInvoiced() || this.noProjects());

  // Forms
  public formControlNames: FormControlNameRecord = {
    Project: 'project',
    Task: 'task',
    Description: 'description',
    DateFrom: timeDialogFormControls.DateFrom,
    DateTo: timeDialogFormControls.DateTo,
    TimeFrom: timeDialogFormControls.TimeFrom,
    TimeTo: timeDialogFormControls.TimeTo,
    Interval: timeDialogFormControls.Interval,
    TimeOfDay: timeDialogFormControls.TimeOfDay,
    Recurrence: 'recurrence',
  };

  public projects$: Observable<GroupResult[]>;
  public tasks: DropdownOption[] = [];
  public readonly faInfoCircle = faInfoCircle;
  private readonly subs = new SubSink();
  private readonly customFormValidators = inject(CustomFormValidators);

  // DI
  private readonly projectTransformationService = inject(ProjectTransformationService);

  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly projectsHttpService: ProjectsHttpService,
    private readonly tasksHttpService: TasksHttpService,
    private readonly projectDataService: ProjectDataService,
  ) {
    this.subs.sink = toObservable(this.isDisabled).subscribe((shouldDisable) => {
      const form = this.projectTypeForm();

      if (shouldDisable) {
        form.disable({ emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.initForm();

    this.fixIntervalDropdownVisible.set(
      this.selectedTimeBlock()?.timeBlockModel.fixInterval !== null,
    );

    this.loadProjects();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public projectChanged(selProject: DropdownOption): void {
    const projectId = selProject.value !== null ? +selProject.value : null;
    const tasks$ = projectId
      ? observableToDropdownOptions(this.tasksHttpService.getTasks(projectId))
      : of([]);

    this.loading$.emit(true);

    tasks$.pipe(take(1)).subscribe((taskOptions) => {
      this.tasks = taskOptions;
      const selectedOption = taskOptions.length > 0 ? taskOptions[0] : [];
      this.projectTypeForm().controls.task.setValue(selectedOption);
      this.loading$.emit(false);
    });
  }

  private initForm(): void {
    const timeBlockModel = this.selectedTimeBlock().timeBlockModel as TimeBlockProjectModel;

    let selectedProject: DropdownOption;
    let selectedTask: DropdownOption;
    if (timeBlockModel.type === TimeBlockType.ExistingBlock) {
      selectedProject = entityToDropdownOption(timeBlockModel.project);
      selectedTask = entityToDropdownOption(timeBlockModel.task);
    }

    const timeBlockProjectContentModel = this.selectedTimeBlock()
      ?.timeBlockModel as TimeBlockProjectModel;

    let timeOfDay = TimeOfDayValues[0];
    if (this.selectedTimeBlock().timeBlockModel.fixInterval) {
      timeOfDay = TimeOfDayValues.find(
        (val) => val.value === this.selectedTimeBlock().timeBlockModel.fixInterval,
      );
    }

    const deactivateSelection =
      timeBlockProjectContentModel.isActive &&
      timeBlockProjectContentModel.source === TimeBlockSource.TrackerSource;

    this.projectTypeForm.set(
      this.formBuilder.group(
        {
          [this.formControlNames.Project]: [
            { value: selectedProject, disabled: deactivateSelection },
          ],
          [this.formControlNames.Task]: [{ value: selectedTask, disabled: deactivateSelection }],
          [this.formControlNames.Description]: [timeBlockProjectContentModel?.description],
          [this.formControlNames.DateFrom]: [this.selectedTimeBlock()?.timeBlockModel.start],
          [this.formControlNames.DateTo]: [this.selectedTimeBlock()?.timeBlockModel.end],
          [this.formControlNames.TimeFrom]: [this.selectedTimeBlock()?.timeBlockModel.start],
          [this.formControlNames.TimeTo]: [this.selectedTimeBlock()?.timeBlockModel.end],
          [this.formControlNames.Interval]: [
            this.selectedTimeBlock()?.timeBlockModel.fixInterval
              ? IntervalTypes.FixInterval
              : IntervalTypes.VariableInterval,
          ],
          [this.formControlNames.TimeOfDay]: [timeOfDay],
          [this.formControlNames.Recurrence]: [null],
        },
        {
          validators: [
            this.customFormValidators.endAfterStart.bind(this.customFormValidators),
            this.customFormValidators.timeBlockDurationTooSmall.bind(
              this.customFormValidators,
              this.selectedTimeBlock()?.timeBlockModel.fixInterval !== null,
            ),
          ],
        },
      ),
    );
  }

  private loadProjects(): void {
    this.projectsHttpService
      .getProjects(null, ProjectStatusFilter.Active)
      .pipe(
        take(1),
        tap((projectModels: ProjectModel[]) => {
          if (projectModels.length > 0) {
            let index = 0;
            const existing =
              this.selectedTimeBlock()?.timeBlockModel.type === TimeBlockType.ExistingBlock &&
              this.selectedTimeBlock()?.timeBlockContentType === TimeBlockContentType.Project;

            if (existing) {
              const selectedProjId = (
                this.selectedTimeBlock()?.timeBlockModel as TimeBlockProjectModel
              ).project.id;
              index = projectModels.findIndex((project) => project.id === selectedProjId);
            }

            this.tasks = this.projectDataService
              .getTasks(projectModels[index], null, true)
              .map((task) => entityToDropdownOption(task));

            if (existing) {
              return;
            }

            const selectedProject = entityToDropdownOption(projectModels[0]);
            const selectedTask = entityToDropdownOption(projectModels[0].tasks[0]);
            this.projectTypeForm().controls.project.setValue(selectedProject);
            this.projectTypeForm().controls.task.setValue(selectedTask);
          }

          this.noProjects.set(projectModels.length === 0);
        }),
      )
      .subscribe((projects) => {
        const groupedProjectsMap =
          this.projectTransformationService.groupProjectsByClients(projects);
        this.projects$ = recordToGroupedDropdownOptions(groupedProjectsMap).pipe(
          map((options) => groupBy(options, [{ field: 'category' }])),
        ) as Observable<GroupResult[]>;
      });
  }
}
